import type Match2EditorScene from 'src/game/scenes/Match2EditorScene';
import { getApplication } from 'src/lib/utils';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import type { Cell } from '../game/components/MapComponent';
import type {
  BlockGenAlgorithm,
  BlockGenAlgorithmDef,
  MapDef,
} from '../game/defs/map';
import type { GameEvent } from '../game/defs/event';
import { getGameScene } from '../game/getGameScene';
import type { GameScene } from '../game/GameScene';
import { waitFor } from '../lib/util/waitFor';
import {
  getAutoPlayDecision,
  type AutoPlayDecisionReason,
} from './editor.autoplay';
import { editorOpenPlaytestGame } from './editor.utils';

export type Match2AutomationFinishMode = 'win' | 'lose' | 'quit';
export type Match2AutomationResult = ReturnType<GameScene['getResult']>;

export type Match2AutomationLoadLevelArgs = {
  level: number;
  data?: string | MapDef;
  blockGen?: BlockGenAlgorithmDef;
};

export type Match2AutomationStatus = {
  screen: 'editor' | 'playtest' | 'unknown';
  level?: number;
  active?: boolean;
  phase?: string;
  result?: Match2AutomationResult;
  moves?: number;
  remainingGoals?: number;
};

export type Match2AutomationAutoPlayArgs = {
  maxMoves?: number;
  timeoutMs?: number;
  moveDelayMs?: number;
  trace?: boolean;
};

export type Match2AutomationAutoPlayStopReason =
  | 'complete'
  | 'max-moves'
  | 'no-move'
  | 'timeout';

export type Match2AutomationAutoPlayMove = {
  kind: 'power' | 'group' | 'booster';
  reason?: AutoPlayDecisionReason;
  blockId: string;
  column: number;
  row: number;
  boosterId?: string;
  groupSize?: number;
  obstacleCount?: number;
  goalHits?: number;
  movesBefore: number;
  remainingGoalsBefore: number;
  goalsCleared?: number;
  powersActivated?: number;
  combosActivated?: number;
};

export type Match2AutomationAutoPlayResult = Match2AutomationStatus & {
  reason: Match2AutomationAutoPlayStopReason;
  movesUsed: number;
  tapCount: number;
  goalCount: number;
  powerCount: number;
  comboCount: number;
  elapsedMs: number;
  lastMove?: Match2AutomationAutoPlayMove;
  trace?: Match2AutomationAutoPlayMove[];
};

export type Match2AutomationApi = {
  loadLevel(
    args: Match2AutomationLoadLevelArgs,
  ): Promise<Match2AutomationStatus>;
  openPlaytest(
    selectedPowerups?: Match2PowerupType[],
  ): Promise<Match2AutomationStatus>;
  waitForGameReady(timeoutMs?: number): Promise<Match2AutomationStatus>;
  autoPlay(
    args?: Match2AutomationAutoPlayArgs,
  ): Promise<Match2AutomationAutoPlayResult>;
  finish(mode: Match2AutomationFinishMode): Promise<Match2AutomationStatus>;
  returnToEditor(): Promise<Match2AutomationStatus>;
  status(): Match2AutomationStatus;
};

type AutoPlayInputPhase = {
  _onTap(cell: Cell): void;
};

type AutoPlayPhaseSystem = {
  phase: string;
  _inputPhase: AutoPlayInputPhase;
};

declare global {
  interface Window {
    __match2Automation?: Match2AutomationApi;
  }
}

let editorScene: Match2EditorScene | undefined;
let currentLevel = 0;
let currentBlockGen: BlockGenAlgorithmDef | undefined;

export function installMatch2EditorAutomation(scene: Match2EditorScene) {
  if (!process.env.IS_DEVELOPMENT) return;

  editorScene = scene;
  window.__match2Automation = {
    loadLevel,
    openPlaytest,
    waitForGameReady,
    autoPlay,
    finish,
    returnToEditor,
    status: getStatus,
  };
}

async function loadLevel(args: Match2AutomationLoadLevelArgs) {
  const scene = await waitForEditorReady();
  currentLevel = args.level;

  if (scene.level !== args.level) {
    await scene.editor.changeLevel(args.level);
  }

  if (args.data !== undefined) {
    await scene.editor.paste(
      typeof args.data === 'string' ? args.data : JSON.stringify(args.data),
    );
  }

  if (args.blockGen) {
    const mapDef = scene.editor.getMapDef();
    mapDef.blockGen = args.blockGen;
    scene.editor.setMapDef(mapDef);
    currentBlockGen = args.blockGen;
  } else {
    currentBlockGen = scene.editor.getMapDef().blockGen;
  }

  return getStatus();
}

async function openPlaytest(selectedPowerups: Match2PowerupType[] = []) {
  const scene = await waitForEditorReady();
  currentLevel = scene.level;

  await editorOpenPlaytestGame(
    scene.level,
    scene.editor.getMapDef(),
    selectedPowerups,
  );

  const status = await waitForGameReady();
  forcePlaytestBlockGen();
  return status;
}

async function waitForGameReady(timeoutMs = 30000) {
  const startedAt = Date.now();
  let readySince = 0;
  let inputPhaseFrames = 0;

  while (Date.now() - startedAt < timeoutMs) {
    const game = getGameScene();
    const isReady =
      game?.active &&
      game.mapDef &&
      game.sessionEntity?.c?.map &&
      !game.isApplyingExtraPowerups;

    if (isReady) {
      readySince = readySince || Date.now();
      inputPhaseFrames =
        game.phaseSystem.phase === 'input' ? inputPhaseFrames + 1 : 0;

      if (inputPhaseFrames >= 3 || Date.now() - readySince > 2000) {
        await waitFor(100);
        return getStatus();
      }
    }

    await waitFor(100);
  }

  throw new Error(`Timed out waiting for Match2 playtest after ${timeoutMs}ms`);
}

async function finish(mode: Match2AutomationFinishMode) {
  const game = getRequiredGameScene();

  switch (mode) {
    case 'win':
      if (!game.cheatSystem) {
        throw new Error(
          'Cannot finish Match2 playtest: cheatSystem is unavailable',
        );
      }
      await game.cheatSystem.winGame();
      break;
    case 'lose':
      if (!game.cheatSystem) {
        throw new Error(
          'Cannot finish Match2 playtest: cheatSystem is unavailable',
        );
      }
      await game.cheatSystem.loseGame();
      break;
    case 'quit':
      game.finish();
      break;
    default:
      mode satisfies never;
  }

  await waitFor(250);
  return getStatus();
}

async function autoPlay(args: Match2AutomationAutoPlayArgs = {}) {
  const game = getRequiredGameScene();
  const startedAt = Date.now();
  const deadline = startedAt + (args.timeoutMs ?? 120000);
  const maxMoves = args.maxMoves ?? 1000;
  const moveDelayMs = args.moveDelayMs ?? 100;
  const movesAtStart = game.moves;
  const trace: Match2AutomationAutoPlayMove[] | undefined = args.trace
    ? []
    : undefined;
  let goalCount = 0;
  let powerCount = 0;
  let comboCount = 0;
  let tapCount = 0;
  let actionCount = 0;
  let lastMove: Match2AutomationAutoPlayMove | undefined;
  let activeMove: Match2AutomationAutoPlayMove | undefined;
  const onEvent = (event?: GameEvent) => {
    if (!event) return;

    if (event.id === 'goal') {
      goalCount += 1;
      if (activeMove) {
        activeMove.goalsCleared = (activeMove.goalsCleared ?? 0) + 1;
      }
    } else if (event.id === 'power') {
      powerCount += 1;
      if (activeMove) {
        activeMove.powersActivated = (activeMove.powersActivated ?? 0) + 1;
      }
    } else if (event.id === 'combo') {
      comboCount += 1;
      if (activeMove) {
        activeMove.combosActivated = (activeMove.combosActivated ?? 0) + 1;
      }
    }
  };

  game.events.subscribe(onEvent);

  try {
    while (Date.now() < deadline) {
      if (game.getResult() !== 'quit') {
        return getAutoPlayResult(
          game,
          'complete',
          startedAt,
          movesAtStart,
          tapCount,
          goalCount,
          powerCount,
          comboCount,
          lastMove,
          trace,
        );
      }

      if (tapCount >= maxMoves) {
        return getAutoPlayResult(
          game,
          'max-moves',
          startedAt,
          movesAtStart,
          tapCount,
          goalCount,
          powerCount,
          comboCount,
          lastMove,
          trace,
        );
      }

      const ready = await waitForAutoPlayInput(game, deadline);
      if (!ready) break;

      if (game.getResult() !== 'quit') {
        return getAutoPlayResult(
          game,
          'complete',
          startedAt,
          movesAtStart,
          tapCount,
          goalCount,
          powerCount,
          comboCount,
          lastMove,
          trace,
        );
      }

      const decision = getAutoPlayDecision(game, { actionCount });
      if (!decision) {
        await game.updateRound();
        await waitFor(250);
        const reason = game.getResult() === 'quit' ? 'no-move' : 'complete';
        return getAutoPlayResult(
          game,
          reason,
          startedAt,
          movesAtStart,
          tapCount,
          goalCount,
          powerCount,
          comboCount,
          lastMove,
          trace,
        );
      }

      const { target } = decision;
      const movesBefore = game.moves;
      lastMove = {
        kind: target.kind,
        reason: decision.reason,
        blockId: target.blockId,
        column: target.column,
        row: target.row,
        boosterId: target.kind === 'booster' ? target.boosterId : undefined,
        groupSize: target.kind === 'group' ? target.groupSize : undefined,
        obstacleCount:
          target.kind === 'booster' ? target.obstacleCount : undefined,
        goalHits: decision.goalHits,
        movesBefore,
        remainingGoalsBefore: getRemainingGoals(game),
      };
      trace?.push(lastMove);
      activeMove = lastMove;

      if (target.kind === 'booster') {
        game.selectedBooster = target.boosterId;
        game.isBoosterTutorialActive = true;
        await game.runBooster(target.boosterId, {
          x: target.column,
          y: target.row,
        });
        actionCount += 1;
        await waitForAutoPlaySettle(game, movesBefore, deadline, {
          waitForMoveChange: false,
        });
        activeMove = undefined;

        if (moveDelayMs > 0) {
          await waitFor(moveDelayMs);
        }

        continue;
      }

      if (!tapCellForAutoPlay(game, target.cell)) {
        activeMove = undefined;
        return getAutoPlayResult(
          game,
          'no-move',
          startedAt,
          movesAtStart,
          tapCount,
          goalCount,
          powerCount,
          comboCount,
          lastMove,
          trace,
        );
      }

      tapCount += 1;
      actionCount += 1;
      await waitForAutoPlaySettle(game, movesBefore, deadline);
      activeMove = undefined;

      if (moveDelayMs > 0) {
        await waitFor(moveDelayMs);
      }
    }

    return getAutoPlayResult(
      game,
      'timeout',
      startedAt,
      movesAtStart,
      tapCount,
      goalCount,
      powerCount,
      comboCount,
      lastMove,
      trace,
    );
  } finally {
    game.events.unsubscribe(onEvent);
  }
}

async function returnToEditor() {
  const game = getGameScene();
  const level = currentLevel || game?.level || 1;

  game?.destroy();

  await getApplication().screenManager.showScreen('minigame', {
    minigame: 'match2-editor',
  });

  const scene = await waitForEditorReady();
  await scene.setLevel(level);

  return getStatus();
}

async function waitForEditorReady(timeoutMs = 30000) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const scene = getEditorScene();
    if (scene?.editor && scene.level > 0) return scene;
    await waitFor(100);
  }

  throw new Error(`Timed out waiting for Match2 editor after ${timeoutMs}ms`);
}

function getEditorScene(): Match2EditorScene | undefined {
  const screen = getApplication().screenManager.screens.minigame;
  const scene = screen?.currentScene;

  if (scene && scene === editorScene) {
    return editorScene;
  }

  if (isMatch2EditorScene(scene)) {
    editorScene = scene;
    return editorScene;
  }

  return editorScene;
}

function getRequiredGameScene(): GameScene {
  const game = getGameScene();
  if (!game) throw new Error('Match2 playtest scene is not active');
  return game;
}

function forcePlaytestBlockGen() {
  const game = getGameScene();
  if (
    !game?.cheatSystem ||
    !currentBlockGen ||
    currentBlockGen === 'rubberbanding'
  ) {
    return;
  }

  game.cheatSystem.setStartAlgorithm(currentBlockGen);
  game.cheatSystem.setBlockGenAlgorithm(currentBlockGen as BlockGenAlgorithm);
}

function getStatus(): Match2AutomationStatus {
  const game = getGameScene();
  if (game) {
    const canReadResult = Boolean(game.active && game.sessionEntity?.c?.map);
    return {
      screen: 'playtest',
      level: game.level || currentLevel,
      active: game.active,
      phase: game.phaseSystem.phase,
      result: canReadResult ? game.getResult() : undefined,
      moves: game.moves,
      remainingGoals: canReadResult ? getRemainingGoals(game) : undefined,
    };
  }

  const scene = getEditorScene();
  if (scene) {
    return {
      screen: 'editor',
      level: scene.level || currentLevel,
    };
  }

  return { screen: 'unknown' };
}

function getRemainingGoals(game: GameScene) {
  const goals = game.sessionEntity.c.map.goals;
  return Object.values(goals).reduce((total, value) => total + (value ?? 0), 0);
}

async function waitForAutoPlayInput(game: GameScene, deadline: number) {
  let inputPhaseFrames = 0;

  while (Date.now() < deadline) {
    if (game.getResult() !== 'quit') return true;

    const isReady =
      game.active &&
      game.phaseSystem.phase === 'input' &&
      game.sessionEntity.c.phase.activations === 0 &&
      !game.isApplyingExtraPowerups;

    if (isReady) {
      inputPhaseFrames += 1;
      if (inputPhaseFrames >= 2) return true;
    } else {
      inputPhaseFrames = 0;
    }

    await waitFor(50);
  }

  return false;
}

async function waitForAutoPlaySettle(
  game: GameScene,
  movesBefore: number,
  deadline: number,
  options: { waitForMoveChange: boolean } = { waitForMoveChange: true },
) {
  let inputPhaseFrames = 0;

  while (Date.now() < deadline) {
    // getResult() reports a loss as soon as moves hit zero. Keep waiting here
    // so final cascades can still resolve and potentially clear the last goals.
    const isReady =
      game.phaseSystem.phase === 'input' &&
      game.sessionEntity.c.phase.activations === 0 &&
      !game.isApplyingExtraPowerups;

    const hasExpectedMoveState =
      !options.waitForMoveChange || game.moves !== movesBefore;

    if (isReady && hasExpectedMoveState) {
      inputPhaseFrames += 1;
      if (inputPhaseFrames >= 2) return;
    } else {
      inputPhaseFrames = 0;
    }

    await waitFor(50);
  }
}

function tapCellForAutoPlay(game: GameScene, cell: Cell) {
  if (game.phaseSystem.phase !== 'input') return false;

  const phaseSystem = game.phaseSystem as unknown as AutoPlayPhaseSystem;
  phaseSystem['_inputPhase']['_onTap'](cell);

  return true;
}

function getAutoPlayResult(
  game: GameScene,
  reason: Match2AutomationAutoPlayStopReason,
  startedAt: number,
  movesAtStart: number,
  tapCount: number,
  goalCount: number,
  powerCount: number,
  comboCount: number,
  lastMove?: Match2AutomationAutoPlayMove,
  trace?: Match2AutomationAutoPlayMove[],
): Match2AutomationAutoPlayResult {
  return {
    ...getStatus(),
    reason,
    movesUsed: Math.max(0, movesAtStart - game.moves),
    tapCount,
    goalCount,
    powerCount,
    comboCount,
    elapsedMs: Date.now() - startedAt,
    lastMove,
    trace,
  };
}

function isMatch2EditorScene(value: unknown): value is Match2EditorScene {
  return isRecord(value) && 'editor' in value && 'setLevel' in value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
